import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth, API } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { DASHBOARD, FORM } from "@/constants/testIds";
import { urgencyOf, formatDate, daysUntil } from "@/lib/dateUtils";
import { priorityStyle } from "@/lib/taskPriority";
import Modal from "@/components/Modal";
import TaskForm from "@/components/TaskForm";
import { WorkOrderClientFiles } from "@/components/OrderDetailsModal";

const URGENCY_TINT = {
  overdue: "border-l-[6px] border-l-[var(--alert-overdue)]",
  today: "border-l-[6px] border-l-[var(--alert-today)]",
  tomorrow: "border-l-[6px] border-l-[var(--alert-tomorrow)]",
  future: "border-l-[6px] border-l-[var(--border)]",
  none: "border-l-[6px] border-l-[var(--border)]",
};

export default function TasksPanel({ employees = [], orders = [], onChanged }) {
  const { isAdmin } = useAuth();
  const { t, lang } = useI18n();

  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterEmp, setFilterEmp] = useState("all");
  const [filterDue, setFilterDue] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  // Sprint O5 — Bulk selection + bulk assign
  const [selected, setSelected] = useState(new Set());
  const [bulkAssignee, setBulkAssignee] = useState("");
  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelected = () => setSelected(new Set());
  const submitBulkAssign = async () => {
    if (selected.size === 0) return;
    try {
      await axios.post(`${API}/tasks/bulk-assign`, {
        task_ids: Array.from(selected),
        employee_id: bulkAssignee || null,
      });
      toast.success(`${selected.size} tarea(s) re-asignada(s)`);
      clearSelected();
      setBulkAssignee("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/tasks`);
      setTasks(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const employeeById = useMemo(() => {
    const m = {};
    employees.forEach((e) => (m[e.id] = e));
    return m;
  }, [employees]);

  const orderById = useMemo(() => {
    const m = {};
    orders.forEach((o) => (m[o.id] = o));
    return m;
  }, [orders]);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return tasks
      .filter((task) => {
        if (filterEmp !== "all") {
          if (filterEmp === "unassigned" && task.employee_id) return false;
          if (filterEmp !== "unassigned" && task.employee_id !== filterEmp) return false;
        }
        if (filterStatus !== "all" && task.status !== filterStatus) return false;
        if (filterDue !== "all") {
          const d = daysUntil(task.due_date);
          if (filterDue === "overdue" && !(d !== null && d < 0)) return false;
          // Sprint 2026-06 — vista Atrasadas: ocultar completadas (historial
          // intacto: siguen visibles eligiendo Estado = Completadas).
          if (filterDue === "overdue" && filterStatus === "all" && task.status === "completed") return false;
          if (filterDue === "today" && d !== 0) return false;
          if (filterDue === "this_week" && !(d !== null && d >= 0 && d <= 7)) return false;
          if (filterDue === "upcoming" && !(d !== null && d > 7)) return false;
        }
        if (q) {
          const order = task.order_id ? orderById[task.order_id] : null;
          const fields = [
            task.name,
            task.product,
            task.task_type,
            task.order_invoice_number,
            task.order_company_name,
            task.order_customer_name,
            task.order_item_description,
            order?.company_name,
            order?.customer_name,
            order?.contact_person,
            order?.invoice_number,
            order?.order_number,
            order?.customer_phone,
            order?.product,
          ];
          if (!fields.some((f) => String(f || "").toLowerCase().includes(q))) return false;
        }
        return true;
      })
      .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
  }, [tasks, filterEmp, filterDue, filterStatus, searchQuery, orderById]);

  const submit = async (payload) => {
    try {
      if (editing?.id) {
        await axios.put(`${API}/tasks/${editing.id}`, payload);
      } else {
        await axios.post(`${API}/tasks`, payload);
      }
      toast.success(t("save"));
      setOpen(false);
      setEditing(null);
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const complete = async (id) => {
    try {
      await axios.post(`${API}/tasks/${id}/complete`);
      toast.success(t("completed"));
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await axios.delete(`${API}/tasks/${id}`);
      toast.success(t("delete"));
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const selectClass =
    "border border-[var(--border)] bg-white px-3 py-2 text-sm font-mono-tech";

  return (
    <div data-testid={DASHBOARD.tasksList} className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="label-overline">TSK / 02</div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">
            {isAdmin ? t("tab_tasks") : t("my_tasks")}
          </h2>
        </div>
        {isAdmin && (
          <button
            data-testid={DASHBOARD.newTaskBtn}
            onClick={() => {
              setEditing({});
              setOpen(true);
            }}
            className="bg-[var(--text-primary)] text-white px-5 py-2.5 font-display font-bold tracking-wide hover:bg-[var(--primary)] transition-colors"
          >
            + {t("new_task")}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row md:items-end gap-3">
        <div className="flex-1 min-w-[200px]">
          <label className="label-overline block mb-1">{t("search")}</label>
          <input
            data-testid={DASHBOARD.tasksSearchInput}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("search_tasks_placeholder")}
            className="w-full border border-[var(--border)] bg-white px-3 py-2 font-display focus:outline-none focus:border-black"
          />
        </div>
        <div className="flex flex-wrap gap-3 items-end">
        {isAdmin && (
          <div>
            <label className="label-overline block mb-1">{t("filter_employee")}</label>
            <select
              data-testid={DASHBOARD.taskFilterEmployee}
              value={filterEmp}
              onChange={(e) => setFilterEmp(e.target.value)}
              className={selectClass}
            >
              <option value="all">{t("all")}</option>
              <option value="unassigned">{t("unassigned")}</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="label-overline block mb-1">{t("filter_due")}</label>
          <select
            data-testid={DASHBOARD.taskFilterDue}
            value={filterDue}
            onChange={(e) => setFilterDue(e.target.value)}
            className={selectClass}
          >
            <option value="all">{t("all")}</option>
            <option value="overdue">{t("overdue")}</option>
            <option value="today">{t("today")}</option>
            <option value="this_week">{t("this_week")}</option>
            <option value="upcoming">{t("upcoming")}</option>
          </select>
        </div>
        <div>
          <label className="label-overline block mb-1">{t("filter_status")}</label>
          <select
            data-testid="task-filter-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className={selectClass}
          >
            <option value="all">{t("all")}</option>
            <option value="pending">{t("task_status_pending")}</option>
            <option value="in_progress">{t("task_status_in_progress")}</option>
            <option value="completed">{t("task_status_completed")}</option>
          </select>
        </div>
        </div>
      </div>

      {/* Sprint O5 — Bulk assign bar */}
      {isAdmin && selected.size > 0 && (
        <div
          data-testid={DASHBOARD.tasksBulkAssignTrigger}
          className="bg-amber-50 border-2 border-amber-500 p-3 flex flex-wrap items-center gap-3"
        >
          <span className="font-display font-bold text-sm">
            {selected.size} tarea{selected.size === 1 ? "" : "s"} seleccionada{selected.size === 1 ? "" : "s"}
          </span>
          <select
            data-testid={DASHBOARD.tasksBulkAssignSelect}
            value={bulkAssignee}
            onChange={(e) => setBulkAssignee(e.target.value)}
            className="border border-black px-2 py-1.5 text-sm font-mono-tech"
          >
            <option value="">— Sin asignar —</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-testid={DASHBOARD.tasksBulkAssignSubmit}
            onClick={submitBulkAssign}
            className="bg-black text-white px-3 py-1.5 font-display font-bold text-sm"
          >
            Asignar
          </button>
          <button
            type="button"
            onClick={clearSelected}
            className="border border-black px-3 py-1.5 font-display font-bold text-sm"
          >
            Cancelar
          </button>
        </div>
      )}

      {loading ? (
        <div className="font-mono-tech text-sm text-[var(--text-secondary)] py-8 text-center">
          LOADING…
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-[var(--border)] bg-white py-16 text-center text-[var(--text-secondary)]">
          {tasks.length === 0 ? t("no_tasks") : t("no_filtered_tasks")}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((task) => {
            const { bucket } = urgencyOf(task.due_date);
            const emp = task.employee_id ? employeeById[task.employee_id] : null;
            const order = task.order_id ? orderById[task.order_id] : null;
            // Fallback to fields enriched by the backend in /api/tasks
            // so employees (who don't have /api/orders access) still see context.
            const invoiceNum = order?.invoice_number || task.order_invoice_number || "";
            const companyName = order?.company_name || task.order_company_name || "";
            const customerName = order?.customer_name || task.order_customer_name || "";
            const hasOrderInfo = Boolean(task.order_id && (invoiceNum || companyName || customerName));
            const done = task.status === "completed";
            const prio = task.priority || "normal";
            const ps = priorityStyle(prio);
            const priorityRing =
              prio === "urgent"
                ? "ring-2 ring-red-500 border-red-300"
                : prio === "priority"
                ? "ring-2 ring-amber-400 border-amber-300"
                : "";
            return (
              <div
                key={task.id}
                data-testid={DASHBOARD.taskCard(task.id)}
                className={`bg-white border border-[var(--border)] ${URGENCY_TINT[bucket]} ${priorityRing} p-4 flex flex-col gap-3 ${
                  done ? "opacity-60" : ""
                }`}
              >
                {hasOrderInfo && (
                  <div
                    data-testid={`task-order-info-${task.id}`}
                    className="-mx-4 -mt-4 px-4 py-2 bg-[var(--bg-secondary,#f5f5f5)] border-b border-[var(--border)]"
                  >
                    {invoiceNum && (
                      <div className="font-mono-tech text-sm font-bold text-[var(--text-primary)]">
                        {t("related_order")}: #{invoiceNum}
                      </div>
                    )}
                    {(customerName || companyName) && (
                      <div className="text-sm text-[var(--text-secondary)] truncate">
                        {t("customer")}: {customerName || companyName}
                        {customerName && companyName && customerName !== companyName
                          ? ` (${companyName})`
                          : ""}
                      </div>
                    )}
                  </div>
                )}
                {task.order_item_note && (
                  <div
                    data-testid={`task-product-note-${task.id}`}
                    className="text-xs font-bold text-amber-900 bg-amber-50 border border-amber-200 px-2 py-1.5"
                  >
                    {t("task_product_note")}: {task.order_item_note}
                  </div>
                )}
                {task.order_id && (
                  <div className="mb-2">
                    <WorkOrderClientFiles orderId={task.order_id} lazy />
                  </div>
                )}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {isAdmin && (
                        <input
                          data-testid={DASHBOARD.tasksBulkSelect(task.id)}
                          type="checkbox"
                          checked={selected.has(task.id)}
                          onChange={() => toggleSelected(task.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="cursor-pointer"
                          title="Seleccionar para asignación masiva"
                        />
                      )}
                      <div className="label-overline">
                        {bucket === "overdue"
                          ? t("overdue")
                          : bucket === "today"
                          ? t("due_today")
                          : bucket === "tomorrow"
                          ? t("due_tomorrow")
                          : t("upcoming")}
                      </div>
                      {prio !== "normal" && (
                        <span
                          data-testid={DASHBOARD.taskPriorityBadge(task.id)}
                          className={`border px-2 py-0.5 text-[10px] font-display font-black tracking-widest ${ps.chip}`}
                        >
                          {lang === "es" ? ps.labelEs : ps.label}
                        </span>
                      )}
                    </div>
                    <h3 className="font-display text-lg font-bold tracking-tight mt-1">
                      {task.name}
                    </h3>
                  </div>
                  <span className="font-mono-tech text-xs text-[var(--text-secondary)] whitespace-nowrap">
                    {formatDate(task.due_date, lang)}
                  </span>
                </div>

                <div className="text-sm space-y-1">
                  {task.task_type && (
                    <div>
                      <span
                        data-testid={`task-type-badge-${task.id}`}
                        className="inline-block border border-[var(--border)] bg-[var(--surface-alt,#f5f5f5)] px-2 py-0.5 text-[10px] font-display font-bold tracking-wider uppercase"
                      >
                        {task.task_type}
                      </span>
                    </div>
                  )}
                  <div>
                    <span className="label-overline mr-2">{t("assigned_to")}</span>
                    <span className="font-medium">{emp ? emp.name : t("unassigned")}</span>
                  </div>
                  {task.product && (
                    <div>
                      <span className="label-overline mr-2">{t("task_description")}</span>
                      <span>{task.product}</span>
                    </div>
                  )}
                  <div>
                    <span className="label-overline mr-2">{t("status")}</span>
                    <span className="font-medium">{t(`task_status_${task.status}`)}</span>
                  </div>
                </div>

                <div className="flex gap-2 mt-auto pt-2">
                  {!done && (
                    <button
                      data-testid={DASHBOARD.markCompleteBtn(task.id)}
                      onClick={() => complete(task.id)}
                      className="flex-1 bg-[var(--success)] text-white px-3 py-2.5 font-display font-bold tracking-wide hover:brightness-95"
                    >
                      ✓ {t("mark_completed")}
                    </button>
                  )}
                  {isAdmin && (
                    <>
                      <button
                        data-testid={FORM.taskEdit(task.id)}
                        onClick={() => {
                          setEditing(task);
                          setOpen(true);
                        }}
                        className="border border-[var(--border)] px-3 py-2 text-sm hover:bg-[var(--surface-alt)]"
                      >
                        {t("edit")}
                      </button>
                      <button
                        data-testid={FORM.taskDelete(task.id)}
                        onClick={() => remove(task.id)}
                        className="border border-[var(--alert-overdue)] text-[var(--alert-overdue)] px-3 py-2 text-sm hover:bg-[var(--alert-overdue)] hover:text-white"
                      >
                        {t("delete")}
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {isAdmin && (
        <Modal
          open={open}
          onClose={() => {
            setOpen(false);
            setEditing(null);
          }}
          title={editing?.id ? t("edit_task") : t("new_task")}
          testId="task-modal"
          wide
        >
          <TaskForm
            initial={editing}
            employees={employees}
            orders={orders}
            onSubmit={submit}
            onCancel={() => {
              setOpen(false);
              setEditing(null);
            }}
          />
        </Modal>
      )}
    </div>
  );
}
